import { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowUp } from 'lucide-react';

/**
 * Floating "scroll to top" button.
 * Appears when user scrolls down, auto-hides after 3s of no interaction.
 */
export function ScrollToTopButton() {
  const [visible, setVisible] = useState(false);
  const [show, setShow] = useState(false);
  const hideTimer = useRef(null);

  const resetHideTimer = useCallback(() => {
    if (!visible) return;
    setShow(true);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setShow(false), 3000);
  }, [visible]);

  useEffect(() => {
    // Throttle scroll handler to once-per-rAF.  React state setters
    // are idempotent (no re-render when value unchanged) but
    // clearTimeout/setTimeout per scroll tick + the function-body work
    // still costs ~0.3 ms per call on mobile — multiplied by 60+ scroll
    // events a second of momentum scroll, that's measurable jank.
    let ticking = false;
    let lastScrolled = null;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        const y = window.scrollY || document.documentElement.scrollTop || 0;
        const scrolled = y > window.innerHeight * 0.6;
        if (scrolled === lastScrolled) return;  // identical → no work
        lastScrolled = scrolled;
        setVisible(scrolled);
        if (scrolled) {
          setShow(true);
          clearTimeout(hideTimer.current);
          hideTimer.current = setTimeout(() => setShow(false), 3000);
        } else {
          setShow(false);
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(hideTimer.current);
    };
  }, []);

  // Re-show on any mouse/touch movement
  useEffect(() => {
    if (!visible) return;

    const events = ['mousemove', 'touchstart', 'touchmove'];
    events.forEach(e => window.addEventListener(e, resetHideTimer, { passive: true }));
    return () => events.forEach(e => window.removeEventListener(e, resetHideTimer));
  }, [visible, resetHideTimer]);

  if (!visible || !show) return null;

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className="fixed bottom-20 right-4 lg:bottom-8 lg:right-8 z-50 w-10 h-10 rounded-full flex items-center justify-center shadow-lg bg-forest-600 text-white hover:scale-110 transition-all duration-300 animate-in fade-in zoom-in-90"
      data-testid="scroll-to-top-btn"
      aria-label="Scroll to top"
    >
      <ArrowUp className="w-5 h-5" />
    </button>
  );
}
